// VA-Massenimport: Upload-Dialog (05.08.2026, kombiniertes ZIP 20.09.2026)
//
// Ein Dialog statt mehrerer: VA-Typ → ZIP → Zielstatus. Alle drei gehören
// fachlich zusammen (sie beschreiben denselben einen Job) und sind zusammen
// klein genug für einen Bildschirm — ein Wizard mit Zurück/Weiter wäre hier
// mehr Bauwerk als Nutzen.
//
// Der Upload selbst startet NICHT den Worker (Backend-Entscheidung 05.08.2026):
// dieser Dialog schreibt nur Datei + Status `pending`. Start ist ein eigener,
// bewusster Klick auf der Job-Übersichtsseite, nachdem die Zeilenzahl sichtbar ist.
//
// EIN ZIP für alles (Entscheidung 20.09.2026, ersetzt eine frühere Version
// mit getrenntem Excel- und Flyer-ZIP-Upload): die Zentrale packt die
// ausgefüllte Vorlage und die Flyer-PDFs selbst in ein ZIP (genau das tut sie
// laut Doku für den reinen Flyer-Fall ohnehin schon) und lädt nur diese eine
// Datei hoch. Ablauf beim Absenden: Presigned-URL anfordern → ZIP direkt zu
// S3 hochladen → Job mit der dabei erzeugten jobId anlegen.
//
// Vorlagen-Download (20.09.2026): das generierte Template liegt unter
// public/templates/ (siehe scripts/generate-va-import-template.py) und wird
// hier als einfacher Link angeboten, damit die Zentrale weiß, welche Spalten
// es gibt, ohne die Datei separat zugeschickt zu bekommen.

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Download } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEventTypes, useUploadEventImport, useUploadZip } from '@/services/event-service';
import { ApiError } from '@/lib/api-client';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ruft nach erfolgreichem Upload mit der neuen Job-Id auf — die Seite muss nichts selbst nachladen. */
  onUploaded: (jobId: string) => void;
}

type TargetStatus = 'initial' | 'approved';

export function EventImportModal({ open, onOpenChange, onUploaded }: Props) {
  const { t } = useTranslation('settings');
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: eventTypes = [] } = useEventTypes();
  const zipMutation = useUploadZip();
  const uploadMutation = useUploadEventImport();

  const [vaType, setVaType] = useState('');
  const [targetStatus, setTargetStatus] = useState<TargetStatus>('initial');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = !!vaType && !!file;
  const isSubmitting = zipMutation.isPending || uploadMutation.isPending;

  const reset = () => {
    setVaType('');
    setTargetStatus('initial');
    setFile(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (!file || !vaType) return;
    setError(null);

    try {
      const jobId = await zipMutation.mutateAsync(file);
      const result = await uploadMutation.mutateAsync({ jobId, filename: file.name, vaType, targetStatus });
      handleOpenChange(false);
      onUploaded(result.jobId);
    } catch (err) {
      // Server-Meldungen sind deutsch und konkret (fehlende Spalten, fehlende
      // Excel-Datei im ZIP) — wörtlich zeigen statt einen generischen Text.
      setError(err instanceof ApiError ? err.message : t('eventImport.uploadFailed'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('eventImport.modal.title')}</DialogTitle>
          <DialogDescription>{t('eventImport.modal.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <a
            href="/templates/veranstaltungen-import-vorlage.xlsx"
            download
            className="flex items-center gap-2 text-sm text-primary underline-offset-2 hover:underline"
          >
            <Download className="h-4 w-4" />
            {t('eventImport.modal.downloadTemplate')}
          </a>

          <ModalField label={t('eventImport.modal.vaType')}>
            <Select value={vaType} onValueChange={setVaType}>
              <SelectTrigger>
                <SelectValue placeholder={t('eventImport.modal.vaTypePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {eventTypes
                  .filter((et) => et.published !== false)
                  .map((et) => (
                    <SelectItem key={et.id} value={et.id}>
                      {et.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </ModalField>

          <ModalField label={t('eventImport.modal.file')}>
            <input
              ref={inputRef}
              type="file"
              accept=".zip,application/zip,application/x-zip-compressed"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Button
              type="button"
              variant="outline"
              className="w-full gap-2"
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-4 w-4" />
              {file ? file.name : t('eventImport.modal.filePlaceholder')}
            </Button>
          </ModalField>

          <ModalField label={t('eventImport.modal.targetStatus')}>
            <Select value={targetStatus} onValueChange={(v) => setTargetStatus(v as TargetStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="initial">{t('eventImport.modal.targetStatusInitial')}</SelectItem>
                <SelectItem value="approved">{t('eventImport.modal.targetStatusApproved')}</SelectItem>
              </SelectContent>
            </Select>
          </ModalField>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('eventImport.modal.cancel')}
          </Button>
          <Button disabled={!canSubmit || isSubmitting} onClick={() => void handleSubmit()}>
            {isSubmitting ? t('eventImport.modal.uploading') : t('eventImport.modal.upload')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModalField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
